import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import type { z } from 'zod';
import {
  MutateSubProjectResponseModel,
  SubProjectFormSchema,
  SubProjectFundInfoFormSchema,
  type SubProjectFundInfoUpdateFormSchema,
  SubProjectUpdateFundBusinessMutationSchema,
  SubProjectsForKSModel,
} from '../projects/_models/sub-project';
import {
  invalidateListMap,
  queryKeyMap,
} from '../review-sheet/utils/query-keys';

const _useSubProjectsForKS = (projectId: string) => {
  return useQuery({
    queryKey: queryKeyMap.subProjects(projectId),
    queryFn: async () => {
      const subProjects = await axiosGet(
        `api/projects/${projectId}/sub-projects/`,
      );

      return SubProjectsForKSModel.parse(subProjects);
    },
    enabled: !!projectId,
  });
};

const _useCreateSubProjectFundInfo = (projectId: string) => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof SubProjectFundInfoFormSchema>,
    ) => {
      const payload = SubProjectFundInfoFormSchema.parse(values);
      const res = await axiosPost(
        `/api/projects/${projectId}/sub-projects/`,
        payload,
      );
      await Promise.all(
        invalidateListMap
          .updateDetailedBreakdown(projectId)
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
      return res;
    },
  });
};

const _useUpdateSubProjectFundInfo = (projectId: string) => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      subProjectId,
      ...values
    }: z.infer<typeof SubProjectFundInfoUpdateFormSchema>) => {
      const payload = SubProjectFundInfoFormSchema.parse(values);
      const res = await axiosPatch(
        `/api/projects/${projectId}/sub-projects/${subProjectId}/`,
        payload,
      );
      await Promise.all(
        invalidateListMap
          .updateDetailedBreakdown(projectId)
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
      return res;
    },
  });
};

const _useUpdateSubProjectFundBusiness = (projectId: string) => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      subProjectId,
      ...values
    }: z.infer<typeof SubProjectUpdateFundBusinessMutationSchema> & {
      subProjectId: string;
    }) => {
      const payload = SubProjectUpdateFundBusinessMutationSchema.parse(values);
      const res = await axiosPatch(
        `/api/projects/${projectId}/sub-projects/${subProjectId}/`,
        payload,
      );
      await Promise.all(
        invalidateListMap
          .updateDetailedBreakdown(projectId)
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
      return res;
    },
  });
};

const _useDeleteSubProject = (projectId: string) => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (subProjectId: string) => {
      const res = await axiosDelete(
        `/api/projects/${projectId}/sub-projects/${subProjectId}/`,
      );
      await Promise.all(
        invalidateListMap
          .updateDetailedBreakdown(projectId)
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
      return res;
    },
  });
};

export const useCreateSubProject = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      projectId,
      ...values
    }: z.infer<typeof SubProjectFormSchema> & { projectId: string }) => {
      const payload = SubProjectFormSchema.parse(values);
      const res = await axiosPost(
        `/api/projects/${projectId}/sub-projects/`,
        // TODO: status should be automatically calculated in the backend
        { ...payload, status: 'new' },
      );
      await Promise.all(
        invalidateListMap
          .updateDetailedBreakdown(projectId)
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
      return MutateSubProjectResponseModel.parse(res);
    },
  });
};
